import React from 'react';
import {
  Card,
  Tag,
  Progress,
  Space,
  Typography,
  Row,
  Col,
  Tooltip,
  Button,
  Divider
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  AimOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text, Title } = Typography;

// Utility function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Color constants for pillars and tiers
const PILLAR_COLORS = {
  'Medication': '#1890ff',
  'Nutrition': '#52c41a',
  'Movement': '#fa8c16',
  'Recovery': '#722ed1',
  'Emotion': '#eb2f96'
};

const TIER_COLORS = {
  1: '#52c41a',
  2: '#1890ff',
  3: '#fa8c16',
  4: '#f5222d'
};

const STATUS_COLORS = {
  'Not Started': 'default',
  'In Progress': 'processing',
  'Completed': 'success',
  'On Hold': 'warning',
  'Cancelled': 'error'
};

/**
 * MultiGoalCard - A specialized card component for displaying SMART goals with multiple patient goals
 * Shows hierarchical structure with MD goal overview and patient goal details
 */
const MultiGoalCard = ({
  goal,
  onEdit,
  onDelete,
  readOnly = false,
  compact = false
}) => {
  if (!goal) return null;

  const getStatusIcon = (status) => {
    const icons = {
      'Not Started': <ClockCircleOutlined />,
      'In Progress': <ClockCircleOutlined />,
      'Completed': <CheckCircleOutlined />,
      'On Hold': <ExclamationCircleOutlined />,
      'Cancelled': <ExclamationCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const isOverdue = () => {
    if (goal.status === 'Completed' || goal.status === 'Cancelled') return false;
    if (!goal.patientGoals || goal.patientGoals.length === 0) return false;
    
    return goal.patientGoals.some(patientGoal => 
      patientGoal.timeline?.targetDate && moment().isAfter(moment(patientGoal.timeline.targetDate))
    );
  };

  const getEarliestTargetDate = () => {
    if (!goal.patientGoals || goal.patientGoals.length === 0) return null;
    
    let earliest = null;
    goal.patientGoals.forEach(patientGoal => {
      if (patientGoal.timeline?.targetDate) {
        const targetDate = moment(patientGoal.timeline.targetDate);
        if (!earliest || targetDate.isBefore(earliest)) {
          earliest = targetDate;
        }
      }
    });
    
    return earliest;
  };

  const earliestTarget = getEarliestTargetDate();
  const overdue = isOverdue();

  return (
    <Card
      className="multi-goal-card"
      size={compact ? "small" : "default"}
      style={{
        marginBottom: 16,
        border: overdue ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
        boxShadow: overdue ? '0 2px 8px rgba(255, 77, 79, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AimOutlined />
            <Text strong>{goal.problem?.problemName || 'SMART Goal'}</Text>
            {goal.problem?.icdCode && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({goal.problem.icdCode})
              </Text>
            )}
          </div>
          <Space>
            <Tag color={STATUS_COLORS[goal.status]} icon={getStatusIcon(goal.status)}>
              {goal.status}
            </Tag>
            {overdue && <Tag color="error">Overdue</Tag>}
          </Space>
        </div>
      }
      extra={
        !readOnly && (
          <Tooltip title="Edit Goal">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit?.(goal);
              }}
            />
          </Tooltip>
        )
      }
    >
      {/* MD Clinical Objective */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: '13px', color: '#666' }}>MD Clinical Objective:</Text>
        <div style={{
          marginTop: 4,
          padding: 8,
          backgroundColor: '#fafafa',
          borderRadius: 4,
          fontSize: '13px'
        }}>
          {decodeHtmlEntities(goal.mdSmartGoalOrder) || 'No clinical objective specified'}
        </div>
      </div>

      {/* Overall Progress */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div>
            <Text strong style={{ fontSize: '12px' }}>Overall Progress:</Text>
            <Progress 
              percent={goal.overallCompletionRate || 0} 
              size="small"
              status={goal.status === 'Completed' ? 'success' : 'active'}
              style={{ marginTop: 4 }}
            />
          </div>
        </Col>
        <Col span={12}>
          <div>
            <Text strong style={{ fontSize: '12px' }}>Patient Goals:</Text>
            <div style={{ marginTop: 4 }}>
              <Text style={{ fontSize: '12px' }}>
                {goal.patientGoals?.length || 0} goals
              </Text>
              {earliestTarget && (
                <div style={{ fontSize: '11px', color: overdue ? '#ff4d4f' : '#666' }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Target: {earliestTarget.format('MMM DD, YYYY')}
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Patient Goals Summary */}
      {goal.patientGoals && goal.patientGoals.length > 0 && (
        <div>
          <Divider style={{ margin: '12px 0' }} />
          <Text strong style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
            Patient Goals Summary:
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {goal.patientGoals.map((patientGoal, index) => (
              <Tooltip
                key={patientGoal._id || index}
                title={
                  <div>
                    <div><strong>Goal:</strong> {patientGoal.smartGoal}</div>
                    <div><strong>Progress:</strong> {patientGoal.completionRate || 0}%</div>
                    <div><strong>Actions:</strong> {patientGoal.actions?.length || 0}</div>
                  </div>
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    backgroundColor: '#fff',
                    border: `1px solid ${PILLAR_COLORS[patientGoal.relatedPillar] || '#d9d9d9'}`,
                    borderRadius: 4,
                    fontSize: '11px'
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: PILLAR_COLORS[patientGoal.relatedPillar] || '#d9d9d9'
                    }}
                  />
                  <Text style={{ fontSize: '11px' }}>
                    {patientGoal.relatedPillar} T{patientGoal.tier}
                  </Text>
                  <Progress
                    type="circle"
                    size={16}
                    percent={patientGoal.completionRate || 0}
                    strokeWidth={8}
                    format={() => null}
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      {/* Compact mode: Show less detail */}
      {compact && goal.patientGoals && goal.patientGoals.length > 3 && (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            +{goal.patientGoals.length - 3} more goals
          </Text>
        </div>
      )}
    </Card>
  );
};

export default MultiGoalCard;
